import { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react'
import { useApi, useAuth } from '../../Hooks'
import { Subscription } from '../../Interfaces/Types'
import { useNavigate } from 'react-router-dom'

export const SubscriptionContext = createContext<any>({})

interface SubscriptionProviderProps {
  children: ReactNode
}

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const { isLoggedIn } = useAuth()
  const { fetchApi } = useApi()
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string | null>(null)
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null)
  const history = useNavigate()

  useEffect(() => {
    async function fetchSubscriptions() {
      if (!isLoggedIn) {
        console.warn('User is not logged in')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const response = await fetchApi({
          endpoint: `/api/subscription`,
          method: 'GET'
        })

        if (subscriptions.length !== response.length || !subscriptions.every((sub, i) => sub.id === response[i].id)) {
          setSubscriptions(response)
        }
      } catch (error) {
        console.error('Failed to fetch subscriptions:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchSubscriptions()
  }, [isLoggedIn, fetchApi])

  useEffect(() => {
    if (selectedSubscriptionId) {
      const subscription = subscriptions.find((sub) => sub.id === selectedSubscriptionId) || null
      setSelectedSubscription(subscription)
    } else {
      setSelectedSubscription(null)
    }
  }, [selectedSubscriptionId, subscriptions])

  const addSubscription = (newSubscription: Subscription) => {
    setSubscriptions((prevSubscriptions) => [...prevSubscriptions, newSubscription])
    handleSetSelectedSubscriptionId(newSubscription.id)
  }

  const handleSetSelectedSubscriptionId = (id: string | null, updateHistory: boolean = true) => {
    setSelectedSubscriptionId(id)
    if (updateHistory) {
      if (id) {
        history(`/subscriptions/${id}`)
      } else {
        history(`/subscriptions/`)
      }
    }
  }

  const removeSubscription = (id: string) => {
    setSubscriptions((prevSubscriptions) => prevSubscriptions.filter((sub) => sub.id !== id))
    if (id === selectedSubscriptionId) {
      handleSetSelectedSubscriptionId(null)
    }
  }

  const contextValue = useMemo(
    () => ({
      subscriptions,
      loading,
      selectedSubscriptionId,
      setSelectedSubscriptionId: handleSetSelectedSubscriptionId,
      selectedSubscription,
      addSubscription,
      removeSubscription
    }),
    [subscriptions, loading, selectedSubscriptionId, selectedSubscription]
  )

  return <SubscriptionContext.Provider value={contextValue}>{children}</SubscriptionContext.Provider>
}

export function useSubscriptions() {
  return useContext(SubscriptionContext)
}
